import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import logo from '../../assets/FinalLOGO.png';
import { Calendar, Phone, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { useSocket } from '../../hooks/useSocket';
import { CardSkeleton } from '../../Components/common/Skeleton';

const CustomerCareDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    contactedLeads: 0,
    screenedLeads: 0,
    totalFollowUps: 0,
    pendingFollowUps: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  const [activePreOpFollowUps, setActivePreOpFollowUps] = useState([]);
  const [pendingRequestFollowUps, setPendingRequestFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  const { socket } = useSocket();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // REAL-TIME AUTO-REFRESH
  useEffect(() => {
    if (!socket) return;

    const handleRealTimeUpdate = (data) => {
      if (data.entityType === 'lead' || data.entityType === 'surgery') {

        if (data.action === 'payment_verified') {
          toast.success(`Payment Received for ${data.patientName}!`, { duration: 5000, position: 'top-right' });
        } else {
          toast.loading(`Lead Update: ${data.patientName} shifted status.`, { duration: 2000 });
        }
        fetchDashboardData();
      }
    };

    socket.on('personal_notification', handleRealTimeUpdate);
    socket.on('system_activity', handleRealTimeUpdate);
    socket.on('lead_updated', handleRealTimeUpdate);
    socket.on('payment_received', handleRealTimeUpdate);

    return () => {
      socket.off('personal_notification', handleRealTimeUpdate);
      socket.off('system_activity', handleRealTimeUpdate);
      socket.off('lead_updated', handleRealTimeUpdate);
      socket.off('payment_received', handleRealTimeUpdate);
    };
  }, [socket]);

  const isDue = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return d <= endOfToday;
  };

  const fetchDashboardData = async () => {
    try {
      const [leadsRes, followUpsRes, preOpFollowUpsRes, pendingReqFollowUpsRes] = await Promise.all([
        api.get('/leads?limit=10'),
        api.get('/follow-ups?status=pending&limit=5'),
        api.get('/leads?hasFollowUp=true&limit=100'),
        api.get('/leads?status=new&hasFollowUp=true&registrationPending=true&limit=20')
      ]);

      const leads = leadsRes.data.data.leads || [];
      const followUps = followUpsRes.data.data.followUps || [];
      const allPreOpFollowUps = preOpFollowUpsRes.data.data.leads || [];
      const reqFollowUps = (pendingReqFollowUpsRes.data.data.leads || [])
        .filter(l => l.nextFollowUpDate)
        .sort((a, b) => new Date(a.nextFollowUpDate) - new Date(b.nextFollowUpDate));

      const sortedFollowUps = allPreOpFollowUps.sort((a, b) => {
        return new Date(a.nextFollowUpDate) - new Date(b.nextFollowUpDate);
      });

      const dueCount = allPreOpFollowUps.filter(l => isDue(l.nextFollowUpDate)).length;

      setStats({
        totalLeads: leads.length,
        newLeads: leads.filter((l) => l.status === 'new').length,
        contactedLeads: leads.filter((l) => l.status === 'contacted').length,
        screenedLeads: leads.filter((l) => l.status === 'screened').length,
        totalFollowUps: followUps.length + allPreOpFollowUps.length,
        pendingFollowUps: followUps.filter((f) => f.status === 'pending').length + dueCount
      });

      setRecentLeads(leads.slice(0, 5));
      setPendingFollowUps(followUps);
      setActivePreOpFollowUps(sortedFollowUps);
      setPendingRequestFollowUps(reqFollowUps);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${color} transition-all hover:shadow-lg`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className="text-3xl sm:text-4xl">{icon}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn p-8">
        <div className="flex items-center gap-6 mb-10">
          <img src={logo} alt="Loading..." className="h-20 w-auto opacity-50 grayscale animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-slate-200 animate-pulse rounded"></div>
            <div className="h-8 w-64 bg-slate-200 animate-pulse rounded-lg"></div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <div className="h-32 bg-white rounded-lg shadow-sm animate-pulse"></div>
          <div className="h-32 bg-white rounded-lg shadow-sm animate-pulse"></div>
          <div className="h-32 bg-white rounded-lg shadow-sm animate-pulse"></div>
          <div className="h-32 bg-white rounded-lg shadow-sm animate-pulse"></div>
          <div className="h-32 bg-white rounded-lg shadow-sm animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-4 sm:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
        <h1 className="text-2xl font-bold text-gray-900 capitalize">{user?.roleDetails?.role_name || user?.role?.replace(/_/g, ' ') || 'Customer Care'} Dashboard</h1>
        <p className="text-gray-600 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100">Welcome, {user?.firstName}</p>
      </div>

      {/* 🚀 QUICK LINK TO ROSTER PREDICTIONS */}
      <div className="bg-[#143E78] rounded-[2rem] p-6 sm:p-8 text-white flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 shadow-xl shadow-blue-900/10 border border-white/5 relative overflow-hidden group">
        <div className="relative z-10 max-w-xl">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[8px] sm:text-[9px] font-black uppercase text-blue-200 bg-white/10 px-2 py-0.5 rounded tracking-widest">Predictive Roster</span>
            <span className="text-[8px] sm:text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded tracking-widest">Email Dispatcher</span>
          </div>
          <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight">Clinical Roster & Predictability Engine</h3>
          <p className="text-xs text-blue-100/70 mt-1.5 font-semibold leading-relaxed">Predict exactly which surgeons and anesthesiologists will become free next, track weekly schedule roster validity, and trigger email updates 2 days before expiry.</p>
        </div>
        <Link
          to="/doctor-availability/forecast"
          className="bg-white hover:bg-blue-50 text-[#143E78] font-black px-6 py-4 rounded-2xl text-xs uppercase tracking-wider active:scale-95 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl shrink-0 z-10"
        >
          <Calendar className="w-4 h-4 text-blue-600" /> Open Forecast Dashboard
        </Link>

        {/* Decorative background blur */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500"></div>
      </div>

      {/* Stats Grid - 2x2 on mobile, 3x1 on medium, 5x1 on large */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
        <StatCard
          title="Total Leads"
          value={stats.totalLeads}
          icon="📋"
          color="border-blue-500"
        />
        <StatCard
          title="New Leads"
          value={stats.newLeads}
          icon="🆕"
          color="border-green-500"
        />
        <StatCard
          title="Contacted"
          value={stats.contactedLeads}
          icon="📞"
          color="border-yellow-500"
        />
        <StatCard
          title="Screened"
          value={stats.screenedLeads}
          icon="✅"
          color="border-purple-500"
        />
        <StatCard
          title="Pending"
          value={stats.pendingFollowUps}
          icon="⏳"
          color="border-red-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Recent Leads</h2>
            <Link to="/leads" className="text-sm text-indigo-600 hover:text-indigo-700 font-bold">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {recentLeads.length > 0 ? (
              recentLeads.map((lead) => (
                <div
                  key={lead._id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all gap-3 border border-gray-100"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 truncate">{lead.patientName}</p>
                    <p className="text-xs text-gray-500">{lead.disease}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-none border-gray-200">
                    {lead.status === 'new' && lead.registrationStep > 1 && (
                      <Link
                        to={`/leads/register-cc/${lead._id}${lead.source === 'DOXEZ' ? '?source=web' : ''}`}
                        className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                        title="Resume Registration"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                    <span
                      className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${lead.status === 'new'
                        ? 'bg-blue-100 text-blue-800'
                        : lead.status === 'contacted'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                        }`}
                    >
                      {lead.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-10">No recent leads</p>
            )}
          </div>
        </div>

        {/* Clinical Follow-ups */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Lead Follow-ups</h2>
            <Link to="/leads" className="text-sm text-indigo-600 hover:text-indigo-700 font-bold">
              Pipeline
            </Link>
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {activePreOpFollowUps.length > 0 ? (
              activePreOpFollowUps.map((lead) => {
                const due = isDue(lead.nextFollowUpDate);
                return (
                  <div key={lead._id} className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 ${due ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'} rounded-xl border transition-all gap-4`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-800 text-sm truncate">{lead.patientName}</p>
                        {due && <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[8px] font-bold rounded uppercase animate-pulse">Due</span>}
                      </div>
                      <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-1 font-medium">
                        <Calendar className="w-3 h-3" /> {new Date(lead.nextFollowUpDate).toLocaleDateString()}
                      </p>
                    </div>
                    <Link to={`/leads/${lead._id}`} className={`w-full sm:w-auto text-center px-4 py-2 ${due ? 'bg-amber-600' : 'bg-slate-800'} text-white text-[10px] font-bold rounded-lg uppercase tracking-wider hover:opacity-90 transition-all`}>
                      {due ? 'Call Now' : 'View'}
                    </Link>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-10">No follow-ups scheduled</p>
            )}
          </div>
        </div>

        {/* Post-Op Monitoring */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Post-Op Monitoring</h2>
            <Link to="/follow-ups" className="text-sm text-indigo-600 hover:text-indigo-700 font-bold">
              View all
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingFollowUps.length > 0 ? (
              pendingFollowUps.map((followUp) => (
                <div
                  key={followUp._id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-all"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 truncate">{followUp.patientName}</p>
                    <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" /> {new Date(followUp.scheduledDate).toLocaleDateString()}
                    </p>
                  </div>
                  <Link
                    to={`/follow-ups/${followUp._id}`}
                    className="p-2 bg-white text-indigo-600 border border-gray-200 rounded-lg shadow-sm hover:text-indigo-800"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-10 md:col-span-2">No pending monitoring</p>
            )}
          </div>
        </div>


      </div>
    </div>
  );
};

export default CustomerCareDashboard;